import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import { FaDna, FaBars, FaTimes } from 'react-icons/fa';

/* ===== NAV LINK FOR LOGGED-IN LAYOUT ===== */
const NavLink = ({ to, children }) => {
    const location = useLocation();
    const isActive = location.pathname.startsWith(to);
    return (
        <Link to={to} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ${isActive ? 'bg-amber-500 text-navy-dark' : 'hover:bg-white/10 text-white'}`}>
            {children}
        </Link>
    );
};

/* ===== PUBLIC LAYOUT (Landing Page, Partners, Privacy, Terms) ===== */
export const PublicLayout = ({ children }) => {
    /* AUTH_AWARE_PUBLIC_NAV */
    const { currentUser, userData, signOut } = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const scrollToSection = (e, sectionId) => {
        e.preventDefault();
        setMobileMenuOpen(false);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        } else {
            // If not on homepage, navigate there first
            window.location.href = '/#' + sectionId;
        }
    };

    return (
        <div className="bg-navy-dark min-h-screen text-white flex flex-col">
            {/* DESKTOP + MOBILE HEADER */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-navy-dark/40 backdrop-blur-lg border-b border-white/10 h-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex justify-between items-center">
                    {/* Logo */}
                    <Link to="/" className="flex items-center self-start">
                        <img src="/PettAI_logo.webp" alt="PettAI — Claim Your Brand Territory" className="h-20 w-auto" />
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center space-x-1 bg-white/5 border border-white/10 rounded-full px-2 py-1">
                        <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="px-3 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all">How It Works</a>
                        <a href="#hubs" onClick={(e) => scrollToSection(e, 'hubs')} className="px-3 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all">Content Hubs</a>
                        <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="px-3 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all">Pricing</a>
                        <a href="#dna-engine" onClick={(e) => scrollToSection(e, 'dna-engine')} className="px-3 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all">DNA Engine</a>
                        <Link to="/partners" className="px-3 py-2 rounded-full text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-all">Partners</Link>
                    </nav>

                    {/* Desktop Auth Buttons */}
                    <div className="hidden md:flex items-center space-x-4">
                        {currentUser ? (
                            <>
                                <span className="hidden lg:inline text-gray-300 text-sm">Hi, {(userData && userData.name && userData.name.split(' ')[0]) || 'there'}</span>
                                <Link to={userData && userData.role === 'agency' ? '/partner-dashboard' : '/dashboard'} className="text-gray-300 hover:text-white font-semibold transition-colors duration-200">Dashboard</Link>
                                <button onClick={() => signOut(navigate)} className="bg-amber-500 hover:bg-amber-600 text-navy-dark font-bold py-2 px-5 rounded-lg transition-transform duration-300 transform hover:scale-105">Sign Out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="text-gray-300 hover:text-white font-semibold transition-colors duration-200">Log In</Link>
                                <Link to="/signup" className="bg-amber-500 hover:bg-amber-600 text-navy-dark font-bold py-2 px-5 rounded-lg transition-transform duration-300 transform hover:scale-105">Start Free Trial</Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Hamburger */}
                    <button
                        className="md:hidden text-white p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-navy-dark border-t border-white/10 px-4 py-6 space-y-4">
                        <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="block text-lg text-gray-300 hover:text-white py-2">How It Works</a>
                        <a href="#hubs" onClick={(e) => scrollToSection(e, 'hubs')} className="block text-lg text-gray-300 hover:text-white py-2">Content Hubs</a>
                        <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="block text-lg text-gray-300 hover:text-white py-2">Pricing</a>
                        <a href="#dna-engine" onClick={(e) => scrollToSection(e, 'dna-engine')} className="block text-lg text-gray-300 hover:text-white py-2">DNA Engine</a>
                        <Link to="/partners" onClick={() => setMobileMenuOpen(false)} className="block text-lg text-gray-300 hover:text-white py-2">Partners</Link>
                        <div className="border-t border-white/10 pt-4 mt-4 space-y-3">
                            {currentUser ? (
                                <>
                                    <Link to={userData && userData.role === 'agency' ? '/partner-dashboard' : '/dashboard'} onClick={() => setMobileMenuOpen(false)} className="block text-lg text-gray-300 hover:text-white py-2">Dashboard</Link>
                                    <button onClick={() => { signOut(navigate); setMobileMenuOpen(false); }} className="block w-full bg-amber-500 text-navy-dark font-bold py-3 rounded-lg text-center text-lg">Sign Out</button>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block text-lg text-gray-300 hover:text-white py-2">Log In</Link>
                                    <Link to="/signup" onClick={() => setMobileMenuOpen(false)} className="block w-full bg-amber-500 text-navy-dark font-bold py-3 rounded-lg text-center text-lg">Start Free Trial</Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </header>

            {/* Main Content — add top padding for fixed header */}
            <main className="flex-grow pt-24">{children}</main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

/* ===== MAIN LAYOUT (Logged-in Founder Dashboard) ===== */
const MainLayout = ({ children }) => {
    const { userData, signOut } = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <div className="bg-navy-dark min-h-screen text-white flex flex-col">
            <header className="bg-white/5 border-b border-white/10 px-4 py-3">
                <div className="container mx-auto flex justify-between items-center">
                    {/* Logo */}
                    <Link to="/" className="flex items-center">
                        <img src="/PettAI_logo.webp" alt="PettAI — Claim Your Brand Territory" className="h-20 w-auto" />
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden md:flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full p-2">
                        <NavLink to={userData?.role === 'agency' ? "/partner-dashboard" : "/dashboard"}>
                            {userData?.role === 'agency' ? "Partner Dashboard" : "Dashboard"}
                        </NavLink>
                        <NavLink to="/content-library">Content Library</NavLink>
                        <NavLink to="/dna-profile">DNA Profile</NavLink>
                    </nav>

                    {/* Desktop User Info */}
                    <div className="hidden md:flex items-center space-x-4">
                        <span className="text-gray-300 text-sm">Welcome, {userData?.name || 'User'}</span>
                        <Link to="/dashboard/billing" className="text-gray-300 hover:text-white text-sm font-semibold transition-colors">Billing</Link>
                        <button onClick={() => signOut(navigate)} className="bg-red-500/80 hover:bg-red-500 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm">Logout</button>
                    </div>

                    {/* Mobile Hamburger */}
                    <button
                        className="md:hidden text-white p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden border-t border-white/10 mt-3 pt-4 pb-2 space-y-3">
                        <Link to={userData?.role === 'agency' ? "/partner-dashboard" : "/dashboard"} onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-white py-2">
                            {userData?.role === 'agency' ? "Partner Dashboard" : "Dashboard"}
                        </Link>
                        <Link to="/content-library" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-white py-2">Content Library</Link>
                        <Link to="/dna-profile" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-white py-2">DNA Profile</Link>
                        <Link to="/dashboard/billing" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-white py-2">Billing</Link>
                        <div className="border-t border-white/10 pt-3 flex justify-between items-center">
                            <span className="text-gray-400 text-sm">{userData?.name || 'User'}</span>
                            <button onClick={() => { signOut(navigate); setMobileMenuOpen(false); }} className="bg-red-500/80 text-white font-semibold py-2 px-4 rounded-lg text-sm">Logout</button>
                        </div>
                    </div>
                )}
            </header>

            <main className="container mx-auto p-4 md:p-8 flex-grow">{children}</main>
            <Footer />
        </div>
    );
};

export default MainLayout;